import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, type Path } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LikertRow, ScaleRow } from './fields';
import { clearDraft, readDraft, writeDraft } from '@/lib/progress-report-draft';
import { isConcurrentSubmitError, progressReportsApi } from '@/services/progress-reports';
import {
    buildSubmitPayload,
    EMPTY_FORM_VALUES,
    toFormValues,
    type ProgressReportFormValues,
} from '@/types/progress-report-form';
import { EMOTION_ROWS, PHYSICAL_ROWS, type ProgressReport } from '@/types/progress-reports';

/**
 * The weekly progress report, as six short steps.
 *
 * Six because no screen should be longer than a phone viewport, and validation
 * runs per step so a client sees "you missed this one" next to the question
 * rather than as a pile of errors after twenty minutes of work.
 *
 * This is a typed form over typed columns. It deliberately does NOT reuse
 * DynamicJournalForm: that component renders database-defined field
 * definitions, and describing statically-known questions through fake template
 * rows would reintroduce exactly the dynamic indirection #189 came out of.
 */

export type SubmitOutcome =
    | { status: 'created'; report: ProgressReport }
    | { status: 'updated'; report: ProgressReport }
    | { status: 'concurrent-submit' };

interface ProgressReportFormProps {
    /** Scopes the draft, so a shared device never shows one client another's answers. */
    clientId: string;
    /**
     * This week's report, if the client has already sent one.
     *
     * Its answers become the form's starting values, so submitting again is an
     * edit of what they sent rather than a blank form that quietly replaces it.
     * Fetched by the page so this component's values can still be read once, on
     * mount, instead of arriving after first render.
     */
    existing: ProgressReport | null;
    onFinished: (outcome: SubmitOutcome) => void;
}

const MAX_TEXT = 2000;
const LONG_TEXT_MESSAGE = `Please keep this under ${MAX_TEXT} characters.`;

type FieldName = keyof ProgressReportFormValues;

const STEPS: readonly { title: string; fields: readonly FieldName[] }[] = [
    {
        title: 'Energy and sleep',
        fields: ['energyLevel', 'sleepQuality'],
    },
    {
        title: 'Physical wellness',
        fields: PHYSICAL_ROWS.map((row) => row.name),
    },
    {
        // painDescription is not listed: the backend accepts an empty string,
        // because "no pain to describe" is an answer rather than a gap.
        title: 'Pain',
        fields: ['painLevel', 'painDescription'],
    },
    {
        title: 'Food reactions',
        fields: ['negativeFoodReactions'],
    },
    {
        title: 'Emotions',
        fields: EMOTION_ROWS.map((row) => row.name),
    },
    {
        title: 'Weight and commitment',
        fields: ['weightKg', 'commitmentLevel', 'questionsOrRemarks'],
    },
];

/** Used by the error summary to name the question a client missed. */
const FIELD_LABELS: Record<string, string> = {
    energyLevel: 'Energy level',
    sleepQuality: 'Sleep quality',
    painLevel: 'Pain level',
    commitmentLevel: 'Commitment and effort',
    weightKg: 'Weight',
    painDescription: 'Describe your pain',
    negativeFoodReactions: 'Negative food reactions',
    questionsOrRemarks: 'Questions or remarks',
    ...Object.fromEntries(PHYSICAL_ROWS.map((row) => [row.name, row.legend])),
    ...Object.fromEntries(EMOTION_ROWS.map((row) => [row.name, row.legend])),
};

function formatSavedAt(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

/** "Tuesday 11 August", for telling a client when they sent this week's report. */
function formatSubmittedOn(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(date);
}

export function ProgressReportForm({ clientId, existing, onFinished }: ProgressReportFormProps) {
    // Read once, on mount. A useState initialiser rather than useMemo because
    // only the former is guaranteed to run exactly once.
    const [restored] = useState(() => readDraft(clientId));
    const [step, setStep] = useState(() => {
        const saved = restored?.step ?? 0;
        return saved >= 0 && saved < STEPS.length ? saved : 0;
    });
    // Restoring silently is unnerving, so the banner says so out loud and
    // offers a way out. Dismissed once acknowledged or started again.
    const [showRestored, setShowRestored] = useState(() => restored !== null);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const {
        control,
        register,
        getValues,
        setValue,
        trigger,
        reset,
        watch,
        clearErrors,
        formState: { errors },
    } = useForm<ProgressReportFormValues>({
        // Stored answers first, then the draft on top. A draft is work in
        // progress on this device and is newer than whatever was last sent, so
        // it wins where the two disagree.
        defaultValues: {
            ...EMPTY_FORM_VALUES,
            ...(existing ? toFormValues(existing) : {}),
            ...(restored?.values ?? {}),
        },
    });

    // Once the report is in, the draft is gone and must not be rewritten by the
    // unmount save below.
    const finishedRef = useRef(false);
    const stepRef = useRef(step);
    stepRef.current = step;

    // Held synchronously, unlike `mutation.isPending`, which is a render value
    // and so is still false for a second tap landing in the same tick. Disabling
    // the button covers the slow case; this covers the fast one.
    const submittingRef = useRef(false);

    const saveDraft = useCallback(
        (atStep: number) => {
            if (finishedRef.current) return;
            writeDraft(clientId, atStep, getValues());
        },
        [clientId, getValues],
    );

    // A phone locking itself fires visibilitychange, not unload, and that is the
    // case the WP actually cares about. Saving on unmount covers navigating away
    // mid-form.
    useEffect(() => {
        const save = () => {
            if (document.visibilityState === 'hidden') saveDraft(stepRef.current);
        };
        document.addEventListener('visibilitychange', save);
        return () => {
            document.removeEventListener('visibilitychange', save);
            saveDraft(stepRef.current);
        };
    }, [saveDraft]);

    const mutation = useMutation({
        mutationFn: (values: ProgressReportFormValues) =>
            progressReportsApi.create(buildSubmitPayload(values)),
        onSuccess: (report) => {
            finishedRef.current = true;
            clearDraft(clientId);
            onFinished({ status: existing ? 'updated' : 'created', report });
        },
        onError: (error: unknown) => {
            // A 409 no longer means "already submitted" — that path is the edit
            // above. It now means only a genuinely concurrent pair of writes,
            // where the unique index let one through. On a slow connection that
            // is this client's own double tap, so their answers are recorded.
            if (isConcurrentSubmitError(error)) {
                finishedRef.current = true;
                clearDraft(clientId);
                onFinished({ status: 'concurrent-submit' });
                return;
            }
            // A real failure, so let them try again.
            submittingRef.current = false;
            setSubmitError(
                'We could not send your report just now. Please check your connection and try again.',
            );
        },
    });

    const goToStep = (next: number) => {
        setStep(next);
        saveDraft(next);
        setSubmitError(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const currentFields = STEPS[step].fields as Path<ProgressReportFormValues>[];

    const handleNext = async () => {
        const valid = await trigger(currentFields, { shouldFocus: true });
        if (!valid) return;
        goToStep(step + 1);
    };

    const handleBack = () => {
        // No validation on the way back: answers are kept, and blocking someone
        // from reviewing an earlier step is how you lose their work.
        goToStep(Math.max(0, step - 1));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (submittingRef.current) return;
        submittingRef.current = true;

        // Validate every step, not just this one: a restored draft can land the
        // client on the last step with an earlier answer since cleared. Stepping
        // through in order finds the earliest gap, which is where to send them.
        // `errors` cannot be read straight after `trigger` — it is the previous
        // render's snapshot — so the return value is what decides.
        let firstBadStep = -1;
        for (let i = 0; i < STEPS.length; i += 1) {
            const stepValid = await trigger(STEPS[i].fields as Path<ProgressReportFormValues>[], {
                shouldFocus: i === step,
            });
            if (!stepValid && firstBadStep === -1) firstBadStep = i;
        }

        if (firstBadStep !== -1) {
            submittingRef.current = false;
            if (firstBadStep !== step) setStep(firstBadStep);
            return;
        }

        mutation.mutate(getValues());
    };

    const startAgain = () => {
        reset(EMPTY_FORM_VALUES);
        clearDraft(clientId);
        setShowRestored(false);
        setStep(0);
    };

    const weightUnavailable = watch('weightUnavailable');

    // Only errors for the step on screen: a restored draft can carry errors for
    // steps the client has not reached yet, and listing those is just noise.
    const stepErrors = STEPS[step].fields
        .filter((field) => errors[field])
        .map((field) => FIELD_LABELS[field] ?? field);

    return (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Not a dead end and not a duplicate. The client sees what they
                sent, can change it, and sending again replaces it. */}
            {existing && (
                <div className="rounded-xl border border-sky-300 bg-sky-50 p-4">
                    <p className="font-medium text-gray-900">This week’s report is already in</p>
                    <p className="mt-1 text-sm text-gray-700">
                        You sent it on {formatSubmittedOn(existing.reportDate)}, and your answers
                        are filled in below. Change anything you would like to correct and send it
                        again — your coach will see the updated report, not a second one.
                    </p>
                </div>
            )}

            {showRestored && restored && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
                    <p className="font-medium text-gray-900">We kept your answers</p>
                    <p className="mt-1 text-sm text-gray-700">
                        You started this report
                        {restored.savedAt ? ` on ${formatSavedAt(restored.savedAt)}` : ''}. Carry on
                        where you left off, or clear it and start again.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            className="min-h-11"
                            onClick={() => setShowRestored(false)}
                        >
                            Carry on
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            className="min-h-11"
                            onClick={startAgain}
                        >
                            Start again
                        </Button>
                    </div>
                </div>
            )}

            <div>
                <div className="flex items-baseline justify-between">
                    <h2 className="text-xl font-bold text-gray-900">{STEPS[step].title}</h2>
                    {/* Announced on change so a screen reader user knows they moved. */}
                    <p aria-live="polite" className="text-sm font-medium text-gray-600">
                        Step {step + 1} of {STEPS.length}
                    </p>
                </div>
                {/* The one true progress bar on the tracker surfaces, and the
                    placement the branding document names: "green for progress
                    bars, #94c740".

                    The fill is outlined because the lime alone does not work
                    here. Against any light track it measures between 1.52:1 and
                    2.00:1, and the boundary between the filled and unfilled
                    portion is the entire reading of a bar, so on its own it
                    fails 1.4.11's 3:1. --brand-bar-edge is 3.94:1 on the fill
                    and 5.98:1 on the track, which makes the extent perceivable
                    without moving off the colour the client asked for. */}
                <div
                    className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-bar-track"
                    role="presentation"
                >
                    <div
                        className="h-full rounded-full bg-brand-bar-fill ring-1 ring-brand-bar-edge transition-all ring-inset"
                        style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                    />
                </div>
            </div>

            {stepErrors.length > 0 && (
                <div
                    role="alert"
                    className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800"
                >
                    <p className="font-semibold">
                        Please check {stepErrors.length === 1 ? 'this answer' : 'these answers'}:
                    </p>
                    <ul className="mt-1 list-disc pl-5">
                        {stepErrors.map((label) => (
                            <li key={label}>{label}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="space-y-8">
                {step === 0 && (
                    <>
                        <ScaleRow
                            control={control}
                            name="energyLevel"
                            legend="How would you rate your energy level this week?"
                            hint="1 = no energy at all, 10 = full of energy"
                            clearErrors={clearErrors}
                        />
                        <ScaleRow
                            control={control}
                            name="sleepQuality"
                            legend="How would you rate your sleep quality this week?"
                            hint="1 = very poor, 10 = excellent"
                            clearErrors={clearErrors}
                        />
                    </>
                )}

                {step === 1 && (
                    <>
                        <p className="text-sm text-gray-600">
                            How often have you experienced each of these this week?
                        </p>
                        {PHYSICAL_ROWS.map((row) => (
                            <LikertRow
                                key={row.name}
                                control={control}
                                name={row.name}
                                legend={row.legend}
                                clearErrors={clearErrors}
                            />
                        ))}
                    </>
                )}

                {step === 2 && (
                    <>
                        <ScaleRow
                            control={control}
                            name="painLevel"
                            legend="What is your overall pain level this week?"
                            hint="1 = no pain, 10 = the worst pain you can imagine"
                            clearErrors={clearErrors}
                        />
                        <div className="space-y-2">
                            <Label htmlFor="painDescription" className="text-base font-medium">
                                Where is the pain, and what does it feel like?
                            </Label>
                            <p id="painDescription-hint" className="text-sm text-gray-500">
                                Leave this empty if you have no pain to describe.
                            </p>
                            <Textarea
                                id="painDescription"
                                rows={4}
                                aria-describedby={
                                    errors.painDescription
                                        ? 'painDescription-hint painDescription-error'
                                        : 'painDescription-hint'
                                }
                                aria-invalid={errors.painDescription ? true : undefined}
                                {...register('painDescription', {
                                    maxLength: { value: MAX_TEXT, message: LONG_TEXT_MESSAGE },
                                })}
                            />
                            {errors.painDescription && (
                                <p
                                    id="painDescription-error"
                                    className="text-sm font-medium text-red-700"
                                >
                                    {errors.painDescription.message}
                                </p>
                            )}
                        </div>
                    </>
                )}

                {step === 3 && (
                    <div className="space-y-2">
                        <Label htmlFor="negativeFoodReactions" className="text-base font-medium">
                            Have any foods disagreed with you this week?
                        </Label>
                        <p id="negativeFoodReactions-hint" className="text-sm text-gray-500">
                            Describe anything that caused a reaction. Leave it empty if nothing did.
                        </p>
                        <Textarea
                            id="negativeFoodReactions"
                            rows={5}
                            aria-describedby={
                                errors.negativeFoodReactions
                                    ? 'negativeFoodReactions-hint negativeFoodReactions-error'
                                    : 'negativeFoodReactions-hint'
                            }
                            aria-invalid={errors.negativeFoodReactions ? true : undefined}
                            {...register('negativeFoodReactions', {
                                maxLength: { value: MAX_TEXT, message: LONG_TEXT_MESSAGE },
                            })}
                        />
                        {errors.negativeFoodReactions && (
                            <p
                                id="negativeFoodReactions-error"
                                className="text-sm font-medium text-red-700"
                            >
                                {errors.negativeFoodReactions.message}
                            </p>
                        )}
                    </div>
                )}

                {step === 4 && (
                    <>
                        <p className="text-sm text-gray-600">
                            How often have you felt each of these this week?
                        </p>
                        {EMOTION_ROWS.map((row) => (
                            <LikertRow
                                key={row.name}
                                control={control}
                                name={row.name}
                                legend={row.legend}
                                clearErrors={clearErrors}
                            />
                        ))}
                    </>
                )}

                {step === 5 && (
                    <>
                        <div className="space-y-2">
                            <Label htmlFor="weightKg" className="text-base font-medium">
                                What is your weight this week, in kilograms?
                            </Label>
                            <p id="weightKg-hint" className="text-sm text-gray-500">
                                For example 72.5. If you have no way to weigh yourself, tick the box
                                below instead of guessing.
                            </p>
                            <Input
                                id="weightKg"
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                max="999.99"
                                className="min-h-11 max-w-40"
                                disabled={weightUnavailable}
                                aria-describedby={
                                    errors.weightKg
                                        ? 'weightKg-hint weightKg-error'
                                        : 'weightKg-hint'
                                }
                                aria-invalid={errors.weightKg ? true : undefined}
                                {...register('weightKg', {
                                    validate: (value, values) => {
                                        if (values.weightUnavailable) return true;
                                        const entered = value.trim();
                                        if (entered === '') return true;
                                        if (!/^\d{1,3}(\.\d{1,2})?$/.test(entered)) {
                                            return 'Please enter a weight in kilograms, for example 72.5, using at most two decimal places.';
                                        }
                                        const kg = Number(entered);
                                        if (kg <= 0 || kg > 999.99) {
                                            return 'Please enter a weight between 0 and 999.99 kg.';
                                        }
                                        return true;
                                    },
                                })}
                            />
                            {errors.weightKg && (
                                <p id="weightKg-error" className="text-sm font-medium text-red-700">
                                    {errors.weightKg.message}
                                </p>
                            )}

                            {/* Decision M made explicit. An empty number input cannot
                                tell "I have no scale" from "I have not got to it yet",
                                and the difference changes how the coach's comparison
                                should read, so it gets its own control. */}
                            <div className="flex min-h-11 items-center gap-3 pt-2">
                                <Checkbox
                                    id="weightUnavailable"
                                    // The 16px default is not a thumb target.
                                    className="size-6"
                                    checked={weightUnavailable}
                                    onCheckedChange={(checked) => {
                                        const skipped = checked === true;
                                        setValue('weightUnavailable', skipped);
                                        if (skipped) {
                                            setValue('weightKg', '', {
                                                shouldValidate: true,
                                            });
                                        }
                                    }}
                                />
                                <Label
                                    htmlFor="weightUnavailable"
                                    className="flex-1 cursor-pointer py-3 text-base"
                                >
                                    I cannot weigh myself this week
                                </Label>
                            </div>
                        </div>

                        <ScaleRow
                            control={control}
                            name="commitmentLevel"
                            legend="How committed did you feel to the programme this week?"
                            hint="1 = not at all, 5 = completely committed"
                            max={5}
                            clearErrors={clearErrors}
                        />

                        <div className="space-y-2">
                            <Label htmlFor="questionsOrRemarks" className="text-base font-medium">
                                Anything you would like to ask or tell your coach?
                            </Label>
                            <Textarea
                                id="questionsOrRemarks"
                                rows={5}
                                aria-describedby={
                                    errors.questionsOrRemarks
                                        ? 'questionsOrRemarks-error'
                                        : undefined
                                }
                                aria-invalid={errors.questionsOrRemarks ? true : undefined}
                                {...register('questionsOrRemarks', {
                                    maxLength: { value: MAX_TEXT, message: LONG_TEXT_MESSAGE },
                                })}
                            />
                            {errors.questionsOrRemarks && (
                                <p
                                    id="questionsOrRemarks-error"
                                    className="text-sm font-medium text-red-700"
                                >
                                    {errors.questionsOrRemarks.message}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>

            {submitError && (
                <p role="alert" className="text-sm font-medium text-red-700">
                    {submitError}
                </p>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 px-6"
                    onClick={handleBack}
                    disabled={step === 0}
                >
                    Back
                </Button>

                {step < STEPS.length - 1 ? (
                    <Button type="button" className="min-h-11 px-6" onClick={handleNext}>
                        Next
                    </Button>
                ) : (
                    <Button
                        type="submit"
                        className="min-h-11 px-6"
                        // The in-flight guard. A double tap on a slow connection is
                        // the likely cause of a duplicate, so the cheapest fix is to
                        // stop the second tap reaching the network at all.
                        disabled={mutation.isPending}
                    >
                        {mutation.isPending
                            ? 'Sending…'
                            : existing
                              ? 'Update my report'
                              : 'Submit report'}
                    </Button>
                )}
            </div>
        </form>
    );
}
